import {
  APP_TZ,
  cn,
  formatShortDate,
  isDateOnly,
  isDeviceZoneMismatch,
  orgZoneTitle,
  zoneLabel,
  zoneTag,
  zoneTitle,
} from '@/shared/lib'

// Зон в интерфейсе две, и подписи обязаны их различать (канон tzkit).
//   Момент («когда начнётся», ввод и показ времени) идёт в зоне УСТРОЙСТВА:
//     она едет за человеком сама, а сохранённая протухает молча.
//   Календарные сутки («сегодня», «просрочено», границы периода) считаются в
//     зоне ОРГАНИЗАЦИИ: они обязаны быть общими, иначе доска и отчёт разъедутся.
// Раз зона показа не всегда совпадает с зоной суток, её надо назвать — иначе
// «до 18:00» читается как что угодно. Даты уезжают в календари, и там их читают
// уже без интерфейса, поэтому зона обязана быть видимой, а не только в title.
//
// Способов подписи ровно три, и они не смешиваются:
//   ZoneNote        — один раз на список/секцию однотипных моментов;
//   zoneColumnLabel — один раз в заголовке колонки таблицы или meta-ячейки;
//   DateStamp zone  — тег у одиночного момента (карточка, деталь, диалог).
// Календарный день (`due`, `due_date`, `start_date`) не подписывается зоной
// вообще: у дня её нет, и приписать её — соврать.

interface ZoneBadgeProps {
  className?: string
}

/** Бейдж зоны в шапке: в ней показаны моменты («Москва · UTC+3»). */
export function ZoneBadge({ className }: ZoneBadgeProps) {
  const label = zoneLabel()
  const mismatch = isDeviceZoneMismatch()

  return (
    <span
      title={mismatch ? `${zoneTitle()}. ${orgZoneTitle()}.` : `${zoneTitle()}.`}
      className={cn(
        'hidden items-center gap-1 rounded-control border border-border-default px-2 py-1 text-xs sm:inline-flex',
        mismatch ? 'text-text-secondary' : 'text-text-tertiary',
        className,
      )}
    >
      <span>{label}</span>
      {/* Зона устройства не совпала с зоной организации — «сегодня» и
          «просрочено» считаются не по вашим часам, и об этом честнее сказать. */}
      {mismatch && <span aria-hidden="true">·</span>}
      {mismatch && <span className="hidden md:inline">сутки — {zoneTag(new Date(), APP_TZ)}</span>}
    </span>
  )
}

interface ZoneNoteProps {
  /** Что именно показано в зоне: «Время», «Сроки шагов», «Время встреч». */
  what?: string
  /** Хвост про соседние значения без времени: «срок задачи — календарный день». */
  note?: string
  className?: string
}

/**
 * Подпись зоны для списка/секции однотипных моментов — один раз сверху, а не
 * тегом в каждой строке: иначе список превращается в частокол одинаковых тегов.
 */
export function ZoneNote({ what = 'Время', note, className }: ZoneNoteProps) {
  return (
    <p title={`${zoneTitle()}.`} className={cn('text-xs text-text-tertiary', className)}>
      {what} — {zoneTag()}
      {note ? ` · ${note}` : ''}
    </p>
  )
}

/**
 * Заголовок колонки/meta-ячейки с моментами: «Выставлен · UTC+3». Колонке
 * календарных суток (окна и диапазоны фильтра) зона передаётся явно — `APP_TZ`.
 */
export function zoneColumnLabel(label: string, tz?: string): string {
  return `${label} · ${zoneTag(new Date(), tz)}`
}

/**
 * Подпись зоны для полей с временем суток (`datetime-local`, `time`). Поле
 * читает набранное в зоне устройства — ею и подписано. У полей `type="date"`
 * вместо неё идёт `dayFieldHint`: у дня зоны нет.
 */
export function zoneFieldHint(hint?: string): string {
  return hint ? `${hint} · время в вашей зоне (${zoneTag()})` : `Время в вашей зоне (${zoneTag()})`
}

/**
 * Предпросмотр поля `type="date"`: что получится из введённого. У дня времени
 * нет — так и говорим, чтобы «срок 12.08» не читался как «12.08, 00:00».
 */
export function dayFieldHint(value: string): string {
  return isDateOnly(value)
    ? `${formatShortDate(value)} — календарный день, без времени`
    : 'Календарный день, без времени'
}
